// ────────────────────────────────────────────────────────────────────────
// Powerful, fully-offline laptop-spec field extractor.
//
// This is the "brain" behind the OCR Fill button. It takes the raw OCR text
// (from tesseract.js) plus any reconstructed {label, value} spec rows and
// distils them into the exact form fields — Processor, ClockSpeed, Ram,
// Storage, Display, RefreshRate, GPU, Battery, OS, Name.
//
// No LLM, no network. For this narrow domain it can actually beat a generic
// LLM because every pattern here encodes real-world laptop naming
// conventions (Intel Core i5-1335U 13th Gen, Ryzen 7 7730U, RTX 4060 8GB,
// 16GB LPDDR5 6400MHz, 1920x1200 IPS, 144Hz, …). It reads BOTH the structured
// spec rows and the free OCR text, so it works whether the screenshot was a
// clean spec table or a messy marketing page.
// ────────────────────────────────────────────────────────────────────────

const ORDINALS = { 1: '1st', 2: '2nd', 3: '3rd' };
function ordinal(n) {
  const num = parseInt(n, 10);
  if (ORDINALS[num]) return ORDINALS[num];
  return `${num}th`;
}

// Normalise OCR noise: collapse whitespace, fix common mis-reads, unify units.
function normalize(text) {
  return String(text || '')
    .replace(/’|‘/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/\bG\s?Hz\b/gi, 'GHz')
    .replace(/\bM\s?Hz\b/gi, 'MHz')
    .replace(/\b(\d)\s?Hz\b/gi, '$1Hz')
    .replace(/[ \t]+/g, ' ')
    .trim();
}

// Build one big haystack: every spec value + the raw text. Searching the
// label too (e.g. label "Processor", value "i5") lets a value-only row still
// be found by its surrounding context.
function buildHaystack(rawText, specs) {
  const specText = (specs || []).map(s => `${s.label} ${s.value}`).join('\n');
  return normalize(`${specText}\n${rawText || ''}`);
}

// ── Processor ────────────────────────────────────────────────────────────
// parseCpu recognises a CPU inside ONE chunk of text and always returns either
// a clean canonical string or '' — it never echoes label words, so it can be
// safely run on a labelled value like "Processor Name → <value>".
function parseCpu(text, gen, modelHints) {
  if (!text) return '';
  const t = String(text).replace(/\n/g, ' ');
  const intelModel = modelHints.intel;
  const amdModel = modelHints.amd;

  // Intel Core Ultra 9 185H / Core Ultra 7 155H / Core Ultra 5 125U
  let m = t.match(/\bcore\s*ultra\s*([579])\s*([0-9]{3}[a-z]{0,2})?\b/i);
  if (m) {
    const model = m[2] || intelModel;
    return `Intel Core Ultra ${m[1]}${model ? ` ${model.toUpperCase()}` : ''}${gen}`.trim();
  }

  // Intel Core i9-13900HX / Core i7 1255U / i5-8250U (model adjacent to tier)
  m = t.match(/\b(?:intel\s*)?(?:core\s*)?(i[3579])[\s-]*([0-9]{4,5}[a-z]{1,2})\b/i);
  if (m) return `Intel Core ${m[1].toLowerCase()}-${m[2].toUpperCase()}${gen}`;

  // "Core i5" with the model number elsewhere (Flipkart split-row layout)
  m = t.match(/\b(?:intel\s*)?core\s*(i[3579])\b/i) || t.match(/\b(i[3579])\b(?!\s*[0-9])/i);
  if (m) {
    const tier = m[1].toLowerCase();
    if (intelModel) return `Intel Core ${tier}-${intelModel.toUpperCase()}${gen}`;
    return `Intel Core ${tier}${gen}`.trim();
  }

  // AMD Ryzen AI 9 HX 370 / Ryzen 9 8945HS / Ryzen 7 7840HS / Ryzen 5 (variant elsewhere)
  m = t.match(/\b(?:amd\s*)?ryzen\s*(ai\s*)?([3579])\s*((?:hx\s*)?[0-9]{3,4}[a-z]{0,3})?\b/i);
  if (m) {
    const ai = m[1] ? 'AI ' : '';
    const model = (m[3] || amdModel || '').toUpperCase().replace(/\s+/g, ' ').trim();
    return `AMD Ryzen ${ai}${m[2]}${model ? ` ${model}` : ''}${gen}`.replace(/\s+/g, ' ').trim();
  }

  // AMD Athlon
  m = t.match(/\b(?:amd\s*)?athlon\s*([a-z0-9-]+)?\b/i);
  if (m) return `AMD Athlon${m[1] ? ` ${m[1].toUpperCase()}` : ''}`.trim();

  // Intel Pentium / Celeron
  m = t.match(/\b(pentium|celeron)\s*([a-z]?\d{3,4}[a-z]{0,2})?\b/i);
  if (m) {
    const fam = m[1][0].toUpperCase() + m[1].slice(1).toLowerCase();
    return `Intel ${fam}${m[2] ? ` ${m[2].toUpperCase()}` : ''}`.trim();
  }

  // Qualcomm Snapdragon X Elite / X Plus
  m = t.match(/\bsnapdragon\s*(x\s*(?:elite|plus))\b/i);
  if (m) return `Snapdragon ${m[1].replace(/\s+/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`;

  // Apple silicon — M1/M2/M3/M4 (+ Pro/Max/Ultra)
  m = t.match(/\bapple\s*(m[1234])\s*(pro|max|ultra)?\b/i) || t.match(/\b(m[1234])\s+(pro|max|ultra)\b/i);
  if (m) return `Apple ${m[1].toUpperCase()}${m[2] ? ` ${m[2][0].toUpperCase() + m[2].slice(1)}` : ''}`.trim();

  return '';
}

// Pulls the explicit "Processor Variant" value (e.g. 1335U, 7535HS, 465, HX
// 370). Handles 2-column OCR where the variant's value sits on the next line
// after a merged "Processor Variant   Clock Speed" label line.
function findCpuVariant(hay) {
  const lines = hay.split('\n').map(l => l.trim());
  const pick = (s) => {
    const mm = String(s || '').match(/\b((?:hx\s*)?\d{3,4}[a-z]{0,3})\b/i);
    return mm ? mm[1].toUpperCase().replace(/\s+/g, ' ') : '';
  };
  for (let i = 0; i < lines.length; i++) {
    if (/^processor\s+variant\b/i.test(lines[i])) {
      const rest = lines[i].replace(/^processor\s+variant\s*[:-]?\s*/i, '');
      return pick(rest) || pick(lines[i + 1]) || '';
    }
  }
  return '';
}

function extractProcessor(hay) {
  const t = hay.replace(/\n/g, ' ');
  // Generation, but only a sane CPU-generation number (4th–14th) so "USB 3.2
  // Gen 2" and similar don't leak in.
  const genMatch = t.match(/\b(\d{1,2})\s*(?:st|nd|rd|th)?\s*gen(?:eration)?\b/i);
  const genNum = genMatch ? parseInt(genMatch[1], 10) : 0;
  const gen = (genNum >= 4 && genNum <= 14) ? ` ${ordinal(genNum)} Gen` : '';

  // The labelled "Processor Variant" is the most reliable model source and also
  // covers the new short Ryzen AI numbers (465/365) that have no letter suffix.
  const variant = findCpuVariant(hay);
  const modelHints = {
    intel: (t.match(/\b(\d{4,5}(?:U|HX|HK|HQ|H|P|G[147]|Y|KF|KS|K|F|T))\b/i) || [])[1]
         || (/\b(?:intel|core\s*i|core\s*ultra)\b/i.test(t) ? variant : ''),
    amd: (t.match(/\b(\d{4}(?:HS|HX|GE|H|U|G|E))\b/i) || [])[1]
       || (/\b(?:amd|ryzen)\b/i.test(t) ? variant : ''),
  };

  // 1) the whole haystack, then 2) the specifically-labelled value so a
  //    vertical "Processor Name \n AMD Ryzen 9 8945HS" layout still resolves.
  let r = parseCpu(t, gen, modelHints);
  if (r) return r;
  for (const lab of ['processor name', 'processor', 'cpu', 'processor variant']) {
    r = parseCpu(getLabeled(hay, lab), gen, modelHints);
    if (r) return r;
  }
  return '';
}

// ── CPU clock speed ──────────────────────────────────────────────────────
function extractClockSpeed(hay) {
  const t = hay.replace(/\n/g, ' ');

  const base = t.match(/\bbase\s*(?:frequency|clock|speed)?\s*[:-]?\s*([\d.]+)\s*ghz/i);
  const turbo = t.match(/\b(?:max\.?\s*)?(?:turbo|boost|max|up\s*to)\s*(?:turbo|boost|frequency|clock|speed)?\s*[:-]?\s*([\d.]+)\s*ghz/i);

  const cleanNum = (s) => String(parseFloat(s));
  if (base && turbo && cleanNum(base[1]) !== cleanNum(turbo[1])) {
    return `Base ${cleanNum(base[1])}GHz / Turbo ${cleanNum(turbo[1])}GHz`;
  }
  if (base) return `${cleanNum(base[1])}GHz`;
  if (turbo) return `${cleanNum(turbo[1])}GHz`;

  // Otherwise collect every plausible CPU GHz figure (0.8–6.5GHz) and, if two
  // distinct ones exist, treat the lower as base and the higher as turbo.
  const all = [...t.matchAll(/([\d.]+)\s*ghz/gi)]
    .map(x => parseFloat(x[1]))
    .filter(n => n >= 0.8 && n <= 6.5);
  const uniq = [...new Set(all)].sort((a, b) => a - b);
  if (uniq.length >= 2) return `Base ${uniq[0]}GHz / Turbo ${uniq[uniq.length - 1]}GHz`;
  if (uniq.length === 1) return `${uniq[0]}GHz`;
  return '';
}

// ── RAM ──────────────────────────────────────────────────────────────────
function extractRam(hay) {
  // Capacity is matched line-by-line so the storage row's "512 GB" can't bleed
  // into the RAM row that happens to follow it in the flattened text.
  let cap = '';
  for (const line of hay.split('\n')) {
    const m = line.match(/\b(\d{1,3})\s*gb\s*(?:lp)?ddr/i)               // "16GB DDR4"
           || line.match(/\b(?:ram|memory)\s*[:-]?\s*(\d{1,3})\s*gb/i)    // "RAM 16 GB"
           || line.match(/\b(\d{1,3})\s*gb\s*(?:of\s*)?(?:ram|memory)\b/i); // "8GB RAM"
    if (m) { cap = `${m[1]}GB`; break; }
  }
  // Vertical layout: "RAM" on one line, "16 GB" on the next.
  if (!cap) {
    const v = getLabeled(hay, 'ram') || getLabeled(hay, 'memory');
    const gm = v && v.match(/(\d{1,3})\s*gb/i);
    if (gm) cap = `${gm[1]}GB`;
  }

  const t = hay.replace(/\n/g, ' ');
  const typeM = t.match(/\b(lpddr\s?[0-9]x?|ddr\s?[0-9])\b/i);
  const type = typeM ? typeM[1].toUpperCase().replace(/\s+/g, '') : '';

  // RAM frequency lives in MHz; keep it distinct from the CPU's GHz.
  const freqM = t.match(/\b(\d{4,5})\s*mhz\b/i);
  const freq = freqM ? `${freqM[1]}MHz` : '';

  return [cap, type, freq].filter(Boolean).join(' ');
}

// ── Storage ──────────────────────────────────────────────────────────────
function extractStorage(hay) {
  // Storage capacities are >=100GB (or any TB); this floor stops a loose match
  // from grabbing "16 GB" RAM that happens to sit on the same OCR line as SSD.
  const big = (n, u) => u.toLowerCase() === 'tb' || parseInt(n, 10) >= 100;
  // Work line-by-line so a size and its drive type only pair up when they
  // actually belong together (e.g. "SSD Capacity   512 GB" with a word between).
  for (const line of hay.split('\n')) {
    let m = line.match(/\b(\d{1,4})\s*(gb|tb)\b[^\n]{0,18}?\b(nvme\s*ssd|ssd|nvme|hdd|emmc|sata)\b/i);
    if (m && big(m[1], m[2])) return `${m[1]}${m[2].toUpperCase()} ${m[3].toUpperCase().replace(/\s+/g, ' ')}`;
    m = line.match(/\b(nvme\s*ssd|ssd|nvme|hdd|emmc|sata)\b[^\n]{0,18}?\b(\d{1,4})\s*(gb|tb)\b/i);
    if (m && big(m[2], m[3])) return `${m[2]}${m[3].toUpperCase()} ${m[1].toUpperCase().replace(/\s+/g, ' ')}`;
  }
  // size near an explicit "storage" label (same line)
  const m = hay.replace(/\n/g, ' ').match(/\bstorage\s*[:-]?\s*(\d{1,4})\s*(gb|tb)\b/i);
  if (m && big(m[1], m[2])) return `${m[1]}${m[2].toUpperCase()}`;

  // Vertical layout: "SSD Capacity" on one line, "512 GB" on the next.
  for (const lab of ['ssd capacity', 'hdd capacity', 'storage capacity', 'storage', 'ssd', 'hdd']) {
    const v = getLabeled(hay, lab);
    const sm = v && v.match(/(\d{1,4})\s*(gb|tb)/i);
    if (sm && big(sm[1], sm[2])) {
      const type = /\bnvme\b/i.test(hay) ? 'NVMe SSD'
                 : /\bssd\b/i.test(hay) ? 'SSD'
                 : /\bhdd\b/i.test(hay) ? 'HDD' : '';
      return `${sm[1]}${sm[2].toUpperCase()}${type ? ' ' + type : ''}`;
    }
  }

  // Scrambled 2-column layout fallbacks where size and type never share a line.
  const driveType = () => /\bnvme\b/i.test(hay) ? 'NVMe SSD'
                        : /\bssd\b/i.test(hay) ? 'SSD'
                        : /\bhdd\b/i.test(hay) ? 'HDD' : '';
  // A TB capacity anywhere is storage — RAM is never measured in TB.
  const tb = hay.match(/\b(\d(?:\.\d)?)\s*tb\b/i);
  if (tb) { const ty = driveType(); return `${tb[1]}TB${ty ? ' ' + ty : ''}`; }
  // Otherwise the largest >=128GB capacity, when a drive type is present.
  const gbs = [...hay.matchAll(/\b(\d{3,4})\s*gb\b/gi)].map(x => parseInt(x[1], 10)).filter(n => n >= 128 && n <= 4096);
  if (gbs.length && /\b(?:ssd|hdd|nvme|emmc)\b/i.test(hay)) {
    const ty = driveType();
    return `${Math.max(...gbs)}GB${ty ? ' ' + ty : ''}`;
  }
  return '';
}

// ── Display ──────────────────────────────────────────────────────────────
function extractDisplay(hay) {
  const t = hay.replace(/\n/g, ' ');
  const parts = [];

  // Screen size in inches — "15.6 inch", '15.6"', "(15.6 Inch)". Ignores the
  // "39.62 cm" form because it requires an inch/" unit.
  const sizeM = t.match(/\b(\d{1,2}(?:\.\d)?)\s*(?:inch(?:es)?|["”]|''|\bin\b)/i);
  if (sizeM) parts.push(`${sizeM[1]}"`);

  // Resolution — scan every WxH and keep the first one in a real screen range.
  // This rejects laptop DIMENSIONS like "359 x 254 x 21.7 mm" (width < 800).
  let res = '';
  for (const mm of t.matchAll(/\b(\d{3,4})\s*[x×*]\s*(\d{3,4})\b/gi)) {
    const w = parseInt(mm[1], 10), h = parseInt(mm[2], 10);
    if (w >= 800 && w <= 4000 && h >= 600 && h <= 2400) { res = `${w}x${h}`; break; }
  }
  if (res) parts.push(res);

  const panelM = t.match(/\b(ips|oled|amoled|tn|va|retina)\b/i);
  if (panelM) parts.push(panelM[1].toUpperCase());

  // Quality token only if no explicit resolution was found
  if (!res) {
    const qM = t.match(/\b(uhd|qhd\+?|fhd\+?|wuxga|2\.?5k|4k|2k|hd\+?|full\s*hd)\b/i);
    if (qM) parts.push(qM[1].toUpperCase().replace(/\s+/g, ' '));
  }
  return parts.join(' ');
}

// ── Refresh rate ─────────────────────────────────────────────────────────
function extractRefreshRate(hay) {
  const t = hay.replace(/\n/g, ' ');
  // Explicit "refresh rate" context first.
  let m = t.match(/refresh\s*rate\s*[:-]?\s*(\d{2,3})\s*hz/i)
       || t.match(/(\d{2,3})\s*hz\s*(?:refresh)/i);
  if (m) return `${m[1]}Hz`;
  // Otherwise any standalone Hz value in a plausible panel range (not M/GHz).
  const all = [...t.matchAll(/(?:^|[^mg\d])(\d{2,3})\s*hz\b/gi)]
    .map(x => parseInt(x[1], 10))
    .filter(n => n >= 40 && n <= 480);
  if (all.length) return `${Math.max(...all)}Hz`;
  return '';
}

// ── GPU ──────────────────────────────────────────────────────────────────
function extractGpu(hay) {
  const t = hay.replace(/\n/g, ' ');
  // Dedicated VRAM, often a separate row ("Dedicated Graphic Memory Capacity
  // 4 GB") or a title token ("4 GB Graphics").
  const vramM = t.match(/dedicated\s*graphic\s*memory\s*capacity\s*[:-]?\s*(\d{1,2})\s*gb/i)
             || t.match(/\b(\d{1,2})\s*gb\s*(?:gddr\d?|graphics?)\b/i);
  const vram = vramM ? ` ${vramM[1]}GB` : '';

  // NVIDIA GeForce RTX 4060 8GB / GTX 1650 / MX550
  let m = t.match(/\b(?:nvidia\s*)?(?:geforce\s*)?(rtx|gtx|mx)\s*([0-9]{3,4}\s*(?:ti)?(?:\s*super)?)\s*(?:(\d{1,2})\s*gb)?/i);
  if (m) {
    const series = m[1].toUpperCase();
    const model = m[2].toUpperCase().replace(/\s+/g, ' ').trim();
    const v = m[3] ? ` ${m[3]}GB` : vram;
    return `NVIDIA GeForce ${series} ${model}${v}`.replace(/\s+/g, ' ').trim();
  }
  // AMD Radeon RX 6600M / Radeon 780M
  m = t.match(/\bradeon\s*(rx\s*)?([0-9]{3,4}[a-z]{0,2})\b/i);
  if (m) return `AMD Radeon ${m[1] ? 'RX ' : ''}${m[2].toUpperCase()}${vram}`.replace(/\s+/g, ' ').trim();

  // Intel integrated
  m = t.match(/\b(?:intel\s*)?(iris\s*xe(?:\s*graphics)?|uhd\s*graphics?\s*\d{0,4}|hd\s*graphics?\s*\d{0,4})\b/i);
  if (m) {
    const g = m[1].replace(/\s+/g, ' ').trim();
    return `Intel ${g.replace(/\b\w/g, c => c.toUpperCase())}`;
  }
  // Explicitly integrated / shared
  if (/\b(integrated|shared)\s*(graphics)?\b/i.test(t)) return 'Integrated';
  return '';
}

// ── Battery ──────────────────────────────────────────────────────────────
function extractBattery(hay) {
  const t = hay.replace(/\n/g, ' ');
  const parts = [];
  const cellM = t.match(/\b(\d)\s*[-\s]?cell\b/i);
  if (cellM) parts.push(`${cellM[1]}-cell`);
  const whM = t.match(/\b(\d{2,3}(?:\.\d)?)\s*wh(?:r)?\b/i);
  if (whM) parts.push(`${whM[1]}Wh`);
  if (!whM) {
    const hrM = t.match(/\b(\d{1,2}(?:\.\d{1,2})?)\s*(?:hrs?|hours?)\b/i);
    if (hrM) parts.push(`${hrM[1]} Hrs`);
  }
  return parts.join(' ');
}

// ── Operating system ─────────────────────────────────────────────────────
function extractOs(hay) {
  const t = hay.replace(/\n/g, ' ');
  const wins = [...t.matchAll(/\bwindows\s*(11|10|8\.1|8|7)\s*(home|pro(?:fessional)?|enterprise|education|single\s*language)?/gi)];
  if (wins.length) {
    // Prefer a mention that includes the edition (Home/Pro/…) over a bare one.
    const m = wins.find(x => x[2]) || wins[0];
    let ed = m[2] ? ` ${m[2].replace(/professional/i, 'Pro')}` : '';
    ed = ed.replace(/\b\w/g, c => c.toUpperCase());
    return `Windows ${m[1]}${ed}`.trim();
  }
  if (/\bmac\s*os|macos\b/i.test(t)) {
    const v = t.match(/\bmac\s*os\s*([a-z]+)/i);
    return v ? `macOS ${v[1][0].toUpperCase() + v[1].slice(1)}` : 'macOS';
  }
  if (/\bchrome\s*os\b/i.test(t)) return 'Chrome OS';
  if (/\bubuntu\b/i.test(t)) return 'Ubuntu';
  if (/\bdos\b/i.test(t)) return 'DOS';
  if (/\bno\s*os\b|\bfree\s*dos\b/i.test(t)) return 'No OS';
  return '';
}

// ── Product name cleaner ─────────────────────────────────────────────────
// Strips SEO/spec stuffing from an OCR'd title down to Brand + Model, the
// same way the LLM prompt asked the cloud model to.
const BRANDS = ['HP', 'Dell', 'Lenovo', 'Asus', 'Acer', 'MSI', 'Apple', 'Samsung',
  'LG', 'Microsoft', 'Huawei', 'Honor', 'Toshiba', 'Sony', 'Gigabyte', 'Razer',
  'Infinix', 'Avita', 'Realme', 'Redmi', 'Xiaomi', 'Fujitsu', 'Vaio'];

function cleanName(rawName) {
  let n = normalize(rawName);
  if (!n) return '';
  // cut at the first spec-ish callout
  n = n.split(/\b(?:intel|amd|ryzen|core\s*i|\d+\s*gb|\d+\s*tb|windows|with\s)/i)[0];
  n = n.replace(/\(.*?\)/g, ' ');                 // drop parentheticals
  n = n.replace(/[-–—|].*$/, '');                 // drop everything after a dash/pipe
  n = n.replace(/\b(laptop|notebook|thin and light|business|gaming)\b/gi, ' ');
  n = n.replace(/[ \t]+/g, ' ').trim();
  // If a known brand appears mid-string, start the name at the brand.
  for (const b of BRANDS) {
    const idx = n.toLowerCase().indexOf(b.toLowerCase());
    if (idx > 0) { n = n.slice(idx); break; }
  }
  return n.slice(0, 80).trim();
}

// Exact (case-insensitive) spec-row lookup — used so "Brand" matches the
// product brand row, not "Processor Brand".
function specValue(specs, label) {
  const row = (specs || []).find(s => String(s.label).trim().toLowerCase() === label);
  return row ? normalize(row.value) : '';
}

// Tokens marking the end of the "Brand + Model" part of a title — everything
// from here on is specs/marketing and is trimmed off.
const TITLE_STOP = /\b(?:intel|amd|ryzen|celeron|pentium|core\s*i|\d+\s*gb|\d+\s*tb|windows|\d+(?:\.\d)?\s*(?:inch|cm|")|\d+\s*hz|hexa|octa|quad|dual|with|laptop|notebook)\b/i;

function trimTitle(s) {
  let out = String(s || '').split(TITLE_STOP)[0];
  out = out.replace(/\s*\([^)]{9,}\)/g, ' '); // drop long marketing parens, keep "(2026)"
  out = out.replace(/[-–—|/].*$/, '').replace(/\s+/g, ' ').trim();
  return out;
}

// The product title is almost always the one OCR line that STARTS with a known
// brand and carries the model after it ("MSI Thin A15 AMD Ryzen 5 …"). A bare
// "MSI" line (the value of a "Brand" row) is too short and is skipped.
function titleFromLines(rawText) {
  const re = new RegExp('^\\s*(?:' + BRANDS.join('|') + ')\\b', 'i');
  for (const line of String(rawText || '').split('\n')) {
    if (re.test(line.trim())) {
      const s = trimTitle(line.trim());
      if (s.length >= 5) return s.slice(0, 80);
    }
  }
  return '';
}

function findKnownBrand(text) {
  const m = String(text || '').match(new RegExp('\\b(' + BRANDS.join('|') + ')\\b', 'i'));
  if (!m) return '';
  return BRANDS.find(b => b.toLowerCase() === m[1].toLowerCase()) || m[1];
}

// Reads a labelled value even when the value sits on the NEXT line (the common
// vertical "Series \n Thin A15" screenshot layout), not just "Series: Thin A15".
function getLabeled(rawText, label) {
  const lines = String(rawText || '').split('\n').map(l => l.trim());
  const lab = label.replace(/\s+/g, '\\s+');
  const same = new RegExp('^' + lab + '\\s*[:\\-]?\\s+(.+)$', 'i');
  const only = new RegExp('^' + lab + '\\s*[:\\-]?$', 'i');
  const stop = /\s{2,}|\b(?:brand|series|colou?r|type|model|weight|dimensions|ram|ssd|processor|graphic|operating|warranty|part)\b/i;
  const clean = (v) => v.split(stop)[0].replace(/\s+/g, ' ').trim().slice(0, 40);
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(same);
    if (m) { const v = clean(m[1]); if (v) return v; }
    if (only.test(lines[i])) {
      for (let j = i + 1; j < lines.length; j++) {
        if (lines[j]) { const v = clean(lines[j]); if (v) return v; break; }
      }
    }
  }
  return '';
}

// Best product name, robust to messy OCR layouts:
//   1. the brand-led title line ("MSI Thin A15 …" → "MSI Thin A15")
//   2. structured/labelled Brand + Series (handles value-on-next-line)
//   3. a cleaned OCR title, then Brand + Model Number as a last resort.
function buildName(specs, ocrName, rawText) {
  const fromTitle = titleFromLines(rawText) || titleFromLines(ocrName);
  if (fromTitle) return fromTitle;

  const brand = specValue(specs, 'brand') || getLabeled(rawText, 'brand') || findKnownBrand(rawText);
  const series = specValue(specs, 'series') || getLabeled(rawText, 'series');
  if (brand && series) {
    const r = series.replace(new RegExp('^' + brand + '\\s*', 'i'), '').trim();
    return `${brand} ${r}`.replace(/\s+/g, ' ').trim().slice(0, 80);
  }

  const cleaned = cleanName(ocrName);
  if (cleaned && cleaned.length >= 5) return cleaned;

  if (brand) {
    const model = specValue(specs, 'model name') || getLabeled(rawText, 'model name')
                || specValue(specs, 'model number') || getLabeled(rawText, 'model number');
    if (model) return `${brand} ${model.split('/')[0].trim()}`.replace(/\s+/g, ' ').trim().slice(0, 80);
    return brand;
  }
  return cleaned || '';
}

// ── Public API ───────────────────────────────────────────────────────────
// Returns { name, fields } where fields only contains keys it confidently
// found (empty/blank fields are omitted so the caller's rule-based fallback
// can fill them).
export function extractFieldsFromOcr({ name, specs, rawText }) {
  const hay = buildHaystack(rawText, specs);

  const extractors = {
    Processor:   extractProcessor,
    ClockSpeed:  extractClockSpeed,
    Ram:         extractRam,
    Storage:     extractStorage,
    Display:     extractDisplay,
    RefreshRate: extractRefreshRate,
    GPU:         extractGpu,
    Battery:     extractBattery,
    OS:          extractOs,
  };

  const fields = {};
  for (const [key, fn] of Object.entries(extractors)) {
    const val = fn(hay);
    if (val && val.trim()) fields[key] = val.trim();
  }

  const cleanedName = buildName(specs, name, rawText);
  return { name: cleanedName, fields };
}
